import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Storefront, Coins, MegaphoneSimple } from '@phosphor-icons/react';
import FounderboardCoinIcon from './components/FounderboardCoinIcon';
import api from '@/api';
import ComingSoon from './components/ComingSoon';

/**
 * Module access — admin-controlled open/close switches for whole pages.
 *
 * Admins flip these in Admin → Module Access. A closed module keeps its nav tab
 * (so bookmarks and deep links still resolve) but swaps the page body for the
 * "coming soon" banner. Nothing here needs a redeploy.
 *
 * The registry below mirrors MODULE_REGISTRY in backend/server.py — keep the
 * keys and defaults in sync when adding a module.
 */

export const MODULES = {
  'startup.services':  { label: 'Services',       audience: 'startup', icon: Storefront,      defaultEnabled: true },
  'startup.credit':    { label: 'Founder Credit', audience: 'startup', icon: Coins,           defaultEnabled: true },
  'startup.marketing': { label: 'Marketing',      audience: 'startup', icon: MegaphoneSimple, defaultEnabled: true },
  'startup.coins':     { label: 'Founderboard Coins', audience: 'startup', icon: FounderboardCoinIcon, defaultEnabled: true },
  'vc.studio':         { label: 'Studios',        audience: 'vc',      icon: MegaphoneSimple, defaultEnabled: true },
};

const DEFAULT_BANNER_MESSAGE = "We're putting the finishing touches on this feature. Stay tuned!";
const CACHE_KEY = 'moduleAccess:v1';
const REFRESH_MS = 60000;

const fallbackState = () =>
  Object.fromEntries(
    Object.entries(MODULES).map(([key, m]) => [
      key,
      { key, label: m.label, audience: m.audience, enabled: m.defaultEnabled, banner_title: m.label, banner_message: DEFAULT_BANNER_MESSAGE },
    ])
  );

// A cached copy lets the shell paint the right thing on the very first frame —
// without it every reload flashes a spinner over a page that is almost always open.
const readCache = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    return raw && typeof raw === 'object' ? raw : null;
  } catch {
    return null;
  }
};

const ModuleAccessContext = createContext(null);

export function ModuleAccessProvider({ children }) {
  const cached = useRef(readCache());
  const [modules, setModules] = useState(() => ({ ...fallbackState(), ...(cached.current || {}) }));
  const [loaded, setLoaded] = useState(() => !!cached.current);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/module-access', { headers: { 'X-Suppress-Error-Toast': '1' } });
      const next = r.data?.modules;
      if (next && typeof next === 'object') {
        const merged = { ...fallbackState(), ...next };
        setModules(merged);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(merged)); } catch { /* quota — not fatal */ }
      }
    } catch {
      // Offline or backend down: keep whatever we have. Defaults are "open", so a
      // fetch failure never locks a founder out of a page that is actually live.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, REFRESH_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [refresh]);

  const value = useMemo(() => ({
    modules,
    loaded,
    refresh,
    isEnabled: (key) => modules[key]?.enabled !== false,
    moduleFor: (key) => modules[key] || null,
  }), [modules, loaded, refresh]);

  return <ModuleAccessContext.Provider value={value}>{children}</ModuleAccessContext.Provider>;
}

export function useModuleAccess() {
  return useContext(ModuleAccessContext) || {
    modules: fallbackState(),
    loaded: true,
    refresh: () => {},
    isEnabled: () => true,
    moduleFor: (key) => fallbackState()[key] || null,
  };
}

/**
 * Wraps a route element. Renders `children` while the module is open, and the
 * admin-authored "coming soon" banner while it is closed.
 */
export function ModuleGate({ module: key, accent, children }) {
  const { modules, loaded } = useModuleAccess();
  const state = modules[key];
  const meta = MODULES[key] || {};

  // No cache and no response yet — hold rather than flash a page the admin may
  // have just closed. Only ever visible on a first-ever visit.
  if (!loaded) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-text-secondary text-sm">Loading…</div>
      </div>
    );
  }

  if (state?.enabled === false) {
    return (
      <ComingSoon
        title={state.banner_title || meta.label || 'Coming soon'}
        description={state.banner_message || DEFAULT_BANNER_MESSAGE}
        icon={meta.icon}
        {...(accent ? { accent } : {})}
      />
    );
  }

  return children;
}

export default MODULES;
